import React, { useMemo, useState } from 'react';
import {
  Boxes,
  Bot,
  Cable,
  ChevronRight,
  FileClock,
  FolderCog,
  GitBranch,
  Grid2x2,
  HandCoins,
  Home,
  KeyRound,
  LibraryBig,
  ListChecks,
  Route,
  Settings,
  ShieldCheck,
  Sparkles,
  UserCircle2,
  Workflow,
} from 'lucide-react';
import Header from './Header';
import Sidebar from './Sidebar';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ToastProvider } from './Toast';
import { ThemeProvider, useTheme } from './Theme';

const SEGMENT_LABELS: Record<string, string> = {
  home: 'Home',
  workflows: 'Workflows',
  reports: 'Reports',
  'analytics-hub': 'Analytics Hub',
  'reports-builder': 'Analytics Hub',
  configurations: 'Configurations',
  keys: 'Keys',
  'data-ingestion': 'Data Ingestion',
  'llm-routing': 'LLM Routing',
  'data-vault': 'Data Vault',
  schedules: 'Schedules',
  'knowledge-base': 'Knowledge Base',
  logs: 'Logs',
  'my-account': 'My Account',
  'access-settings': 'Access Settings',
  connections: 'Connections',
  flowbuilder: 'Flow Builder',
  deployments: 'Deployments',
  agents: 'Agents',
  'system-settings': 'System Settings',
  agentStudio: 'Agent Studio',
  applications: 'Applications',
  'intent-orchestration': 'Intent Orchestration',
  chatagent: 'Chat Agents',
  createchatagent: 'Create Chat Agent',
  mcpdashboard: 'MCP Dashboard',
  mcpdevelopers: 'Resource Access',
  mcpresources: 'MCP Resources',
  mcplivemonitor: 'MCP Live Monitor',
  chatbot: 'Chatbot',
};

const prettifySegment = (segment: string): string => {
  if (!segment) return '';
  const known = SEGMENT_LABELS[segment];
  if (known) return known;
  return segment
    .replace(/[-_]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
};

const SEGMENT_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  home: Home,
  workflows: Workflow,
  reports: ListChecks,
  'analytics-hub': ListChecks,
  'reports-builder': ListChecks,
  configurations: FolderCog,
  keys: KeyRound,
  'data-ingestion': Boxes,
  'llm-routing': Route,
  'data-vault': LibraryBig,
  schedules: FileClock,
  'knowledge-base': LibraryBig,
  logs: FileClock,
  'my-account': UserCircle2,
  'access-settings': ShieldCheck,
  connections: Cable,
  flowbuilder: GitBranch,
  deployments: HandCoins,
  agents: Bot,
  'system-settings': Settings,
  agentStudio: Sparkles,
  applications: Grid2x2,
  'intent-orchestration': GitBranch,
  chatagent: Bot,
  createchatagent: Bot,
  chatbot: Bot,
};

const PortalLayoutContent = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { theme } = useTheme();
  const location = useLocation();
  const isFlowBuilderRoute = location.pathname.includes('/flowbuilder');
  const isLightTheme = theme === 'white';
  const breadcrumbItems = useMemo(() => {
    const relativePath = location.pathname.replace(/^\/portal\/?/, '');
    if (!relativePath || relativePath === 'home') {
      return [{ label: 'Home', href: '/portal/home', isLast: true }];
    }

    const rawSegments = relativePath.split('/').filter(Boolean);
    const segments = rawSegments.map((segment, index) => {
      const isIdSegment =
        index > 0 &&
        rawSegments[index - 1] === 'chatbot' &&
        segment.length > 0;
      return {
        segment,
        label: isIdSegment ? 'Edit Chat Agent' : prettifySegment(segment),
      };
    });

    return [{ label: 'Home', href: '/portal/home', isLast: segments.length === 0 }].concat(
      segments.map((item, index) => ({
        label: item.label,
        href: `/portal/${rawSegments.slice(0, index + 1).join('/')}`,
        isLast: index === segments.length - 1,
      }))
    );
  }, [location.pathname]);

  return (
    <ToastProvider>
      <Header />
      <div className={`portal-layout pt-16 flex bg-neutral-950 text-white min-h-screen transition-[padding-left] duration-200 ${collapsed ? 'pl-20' : 'pl-56'}`}>
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
        <main
          className={
            isFlowBuilderRoute
              ? 'flex-1 flex flex-col min-h-0 h-[calc(100vh-4rem)] overflow-hidden p-0'
              : 'flex-1 min-w-0 p-3 overflow-auto min-h-[calc(100vh-4rem)] flex flex-col'
          }
        >
          <div
            className={
              isFlowBuilderRoute
                ? 'flex flex-1 flex-col min-h-0 w-full overflow-hidden'
                : 'portal-content flex-1 flex flex-col min-h-0 w-full min-w-0 max-w-none overflow-x-auto'
            }
          >
            <Outlet />
          </div>
        </main>
      </div>
    </ToastProvider>
  );
};

const PortalLayout = () => {
  return (
    <ThemeProvider>
      <PortalLayoutContent />
    </ThemeProvider>
  );
};

export default PortalLayout;
